import { FC, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { Card, FancyLink, Loader, Message, Title } from "shared/components";
import { Weight } from "shared/assets/icons";
import { Spacer } from "shared/layout";
import { copy } from "shared/assets";
import { getUserCardActions, useFetchingUserDocuments } from "Bands/utils";
import { BAND_PATHS } from "Bands/router";
import { useFullUserProfileContext } from "Bands/contexts";
import { IStatus } from "shared/types";
import {
  UserBodyPreferences,
  UserHotelPreferences,
  UserFlightPreferences,
  LoyaltyPrograms,
} from ".";

const {
  userBodyPreferences: bodyCopy,
  userHotelPreferences: hotelCopy,
  userFlightPreferences: flightCopy,
  profile: { profileIds, preferences },
} = copy;

interface Props {
  canManageProfile?: boolean;
}

export const Preferences: FC<Props> = ({ canManageProfile }) => {
  const history = useHistory();

  const { loyaltyPrograms, hasBodyPreferences, hasHotelPreferences, hasFlightPreferences } =
    useFullUserProfileContext();

  const { fetchingLoyaltyPrograms } = useFetchingUserDocuments();

  const handleEdit = useCallback(() => {
    history.push(BAND_PATHS.EDIT_PROFILE_PREFERENCES);
  }, [history]);

  const actions = useMemo(
    () =>
      canManageProfile
        ? getUserCardActions({
            handleEdit,
          })
        : [],
    [canManageProfile, handleEdit]
  );

  const content = useMemo(() => {
    if (fetchingLoyaltyPrograms) return <Loader />;
    if (
      !hasBodyPreferences &&
      !hasHotelPreferences &&
      !hasFlightPreferences &&
      !loyaltyPrograms?.length
    ) {
      return (
        <Message status={IStatus.warning}>
          {canManageProfile ? (
            <FancyLink to={BAND_PATHS.EDIT_PROFILE_PREFERENCES} status={IStatus.warning}>
              {preferences.empty}
            </FancyLink>
          ) : (
            preferences.emptyView
          )}
        </Message>
      );
    }

    return (
      <Spacer size="xl">
        {hasBodyPreferences && (
          <Spacer size="m">
            <Title asComponent="h3" size="s">
              {bodyCopy.title}
            </Title>
            <UserBodyPreferences />
          </Spacer>
        )}
        {hasHotelPreferences && (
          <Spacer size="m">
            <Title asComponent="h3" size="s">
              {hotelCopy.title}
            </Title>
            <UserHotelPreferences />
          </Spacer>
        )}
        {hasFlightPreferences && (
          <Spacer size="m">
            <Title asComponent="h3" size="s">
              {flightCopy.title}
            </Title>
            <UserFlightPreferences />
          </Spacer>
        )}
        <LoyaltyPrograms />
      </Spacer>
    );
  }, [
    fetchingLoyaltyPrograms,
    hasBodyPreferences,
    hasHotelPreferences,
    hasFlightPreferences,
    loyaltyPrograms?.length,
    canManageProfile,
  ]);

  return (
    <Card
      id={profileIds.preferences}
      title={preferences.title}
      icon={Weight}
      actions={actions}
      actionDataTestId="user_details_actions"
    >
      {content}
    </Card>
  );
};
